import { useCallback, useMemo, useRef } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { ThemePreference } from "@deca/shared-types";
import { ApiProvider } from "./app/ApiProvider";
import { useAuth } from "./app/AuthProvider";
import { ThemeProvider } from "./app/ThemeProvider";
import { useMe, useUpdateThemePreference } from "./api/hooks";
import { AppLayout } from "./components/AppLayout";
import { Spinner } from "./components/ui";
import { LoginPage } from "./pages/LoginPage";
import { ForcePinChangePage } from "./pages/ForcePinChangePage";
import { TenantSelectPage } from "./pages/TenantSelectPage";
import { DashboardPage } from "./pages/DashboardPage";
import { UsersPage } from "./pages/UsersPage";
import { TenantsPage } from "./pages/TenantsPage";
import { PlatformBrandingPage } from "./pages/PlatformBrandingPage";
import { DecaListPage } from "./pages/DecaListPage";
import { DecaCreatePage } from "./pages/DecaCreatePage";
import { DecaDetailPage } from "./pages/DecaDetailPage";
import { CounterpartiesPage } from "./pages/CounterpartiesPage";
import { VehiclesPage } from "./pages/VehiclesPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { AuditPage } from "./pages/AuditPage";
import { ReportsPage } from "./pages/ReportsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { BillingPlansPage } from "./pages/BillingPlansPage";
import { TaxTypesPage } from "./pages/TaxTypesPage";
import { SubscriptionsPage } from "./pages/SubscriptionsPage";
import { ErpExportPage } from "./pages/ErpExportPage";
import { ErpConversionsPage } from "./pages/ErpConversionsPage";
import { NumberingSettingsPage } from "./pages/NumberingSettingsPage";
import { InvoiceTemplatePage } from "./pages/InvoiceTemplatePage";
import { FinancialPage } from "./pages/FinancialPage";
import { BackupPage } from "./pages/BackupPage";
import { StoragePage } from "./pages/StoragePage";
import { OrphanedS3Page } from "./pages/OrphanedS3Page";

/**
 * Composición de la aplicación.
 *
 * El orden de los proveedores no es arbitrario:
 *  AuthProvider (sesión, sin depender del cliente) →
 *  ApiProvider (cliente autenticado con el token de la sesión) →
 *  ThemeProvider (ya puede leer `User.themePreference` y persistir cambios).
 *
 * Las pantallas de acceso se montan **fuera** de `ApiProvider` autenticado:
 * antes de entrar no hay token que adjuntar.
 */
export function App() {
  const auth = useAuth();
  const queryClient = useQueryClient();

  const handleUnauthorized = useCallback(() => {
    // Sesión caducada en mitad de una consulta: se limpia todo y el guardia
    // de rutas devuelve al acceso en lugar de dejar pantallas con errores.
    auth.logout();
    queryClient.clear();
  }, [auth, queryClient]);

  // Ref mutable: el ApiClient siempre lee el token más reciente sin
  // necesidad de recrearse cuando cambia auth.token. Esto evita la race
  // condition donde queryClient.clear() + navigate disparan refetches
  // antes de que React procese el re-render con el nuevo ApiClient.
  const tokenRef = useRef<string | null>(auth.token);
  tokenRef.current = auth.token;          // actualización síncrona en cada render
  const getToken = useCallback(() => tokenRef.current, []);

  if (!auth.isAuthenticated) {
    // Sin `onPersist`: antes de login no hay sesión a la que asociar la
    // preferencia de tema, así que el ciclo de tema en esta pantalla solo
    // cambia el documento y la caché local (ver comentario en ThemeProvider).
    //
    // `ApiProvider` con `getToken: () => null` SÍ se monta aquí (a
    // diferencia del resto de este bloque, que antes no llevaba ninguno):
    // `LoginPage` necesita pedir `/api/v1/branding` (el logo de la app) antes
    // de que exista ninguna sesión, y esa ruta es la única de la API que no
    // exige credencial.
    return (
      <ApiProvider getToken={() => null} onUnauthorized={() => {}}>
        <ThemeProvider>
          <Routes>
            <Route path="/entrar" element={<LoginPage />} />
            <Route path="*" element={<Navigate to="/entrar" replace />} />
          </Routes>
        </ThemeProvider>
      </ApiProvider>
    );
  }

  // key={activeTenantId ?? "platform"} fuerza a React a desmontar y volver a
  // montar AuthenticatedApp (y con él ApiProvider + todo el árbol de hooks)
  // cada vez que cambia el tenant activo. Esto garantiza que:
  //  1. El QueryClient arranca vacío para el nuevo tenant (no hay caché del anterior).
  //  2. Los hooks generan sus queryKeys con el activeTenantId correcto desde
  //     el primer render, sin race conditions de timing entre setState y navigate.
  //  3. tokenRef.current ya contiene el token nuevo antes de que cualquier
  //     hook lance su primera query.
  // El coste es el remount del árbol al cambiar tenant — aceptable ya que
  // cambiar de tenant es una acción explícita y poco frecuente del super-admin.
  return (
    <ApiProvider getToken={getToken} onUnauthorized={handleUnauthorized}>
      <AuthenticatedApp key={auth.activeTenantId ?? "platform"} />
    </ApiProvider>
  );
}

function AuthenticatedApp() {
  const auth = useAuth();
  const me = useMe();
  const updateTheme = useUpdateThemePreference();
  const persistTheme = useCallback(
    (preference: ThemePreference) => updateTheme.mutate(preference),
    [updateTheme],
  );

  const initialPreference = useMemo(() => me.data?.themePreference, [me.data?.themePreference]);

  // Esperamos a que /me resuelva antes de decidir qué mostrar.
  // Sin este guard, el superAdmin ve TenantSelectPage un instante al cargar:
  // auth.needsTenant=true y me.data aún es undefined → isSuperAdmin=false →
  // la condición `needsTenant && !isSuperAdmin` es true → flash del selector.
  // Con el guard, mostramos un spinner neutro hasta tener la respuesta de /me.
  if (me.isLoading) {
    return (
      <ThemeProvider initialPreference={undefined}>
        <div className="flex min-h-screen items-center justify-center">
          <Spinner label="Cargando sesión…" />
        </div>
      </ThemeProvider>
    );
  }

  // PIN temporal generado por un administrador: la API solo deja elegir el PIN
  // propio o cerrar sesión hasta que se haga. Se ofrece solo eso.
  if (me.data?.pinMustChange === true) {
    return (
      <ThemeProvider initialPreference={initialPreference} onPersist={persistTheme}>
        <ForcePinChangePage />
      </ThemeProvider>
    );
  }

  const isSuperAdmin = me.data?.isSuperAdmin ?? false;

  // Un super-admin de plataforma puede no pertenecer a ningún tenant (no es
  // "empleado" de ninguna empresa cliente): necesita entrar al panel para
  // gestionar la lista de empresas sin que se le obligue antes a elegir una
  // sobre la que operar (eso sigue siendo opcional, vía el selector del
  // encabezado). Un usuario normal con varios tenants sí pasa siempre por
  // `TenantSelectPage`: sus pantallas dependen todas de un tenant activo.
  if (auth.needsTenant && !isSuperAdmin) {
    return (
      <ThemeProvider initialPreference={initialPreference} onPersist={persistTheme}>
        <Routes>
          <Route path="*" element={<TenantSelectPage />} />
        </Routes>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider initialPreference={initialPreference} onPersist={persistTheme}>
      <Routes>
        {/*
         * AppLayout es ESTABLE: nunca se desmonta al cambiar needsTenant.
         * Todas las rutas conviven en el mismo árbol — las páginas que
         * requieren tenant activo redirigen internamente si no hay ninguno.
         * Esto evita que AppLayout se remonte (y pierda estado local) cuando
         * el superAdmin cambia de tenant con el selector "Opera como".
         */}
        <Route element={<AppLayout />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/usuarios" element={<UsersPage />} />
          <Route path="/empresas" element={<TenantsPage />} />
          <Route path="/marca-app" element={<PlatformBrandingPage />} />
          <Route path="/planes-facturacion" element={<BillingPlansPage />} />
          <Route path="/tipos-impuesto" element={<TaxTypesPage />} />
          <Route path="/suscripciones" element={<SubscriptionsPage />} />
          <Route path="/exportacion-erp" element={<ErpExportPage />} />
          <Route path="/conversion-erp" element={<ErpConversionsPage />} />
          <Route path="/ajustes-numeracion" element={<NumberingSettingsPage />} />
          <Route path="/plantilla-factura" element={<InvoiceTemplatePage />} />
          <Route path="/deca" element={<DecaListPage />} />
          <Route path="/deca/nuevo" element={<DecaCreatePage />} />
          <Route path="/deca/:id" element={<DecaDetailPage />} />
          <Route path="/contrapartes" element={<CounterpartiesPage />} />
          <Route path="/vehiculos" element={<VehiclesPage />} />
          <Route path="/plantillas" element={<TemplatesPage />} />
          <Route path="/api-keys" element={<ApiKeysPage />} />
          <Route path="/auditoria" element={<AuditPage />} />
          <Route path="/reportes" element={<ReportsPage />} />
          <Route path="/financiero" element={<FinancialPage />} />
          <Route path="/backup" element={<BackupPage />} />
          <Route path="/almacenes" element={<StoragePage />} />
          <Route path="/huerfanos-s3" element={<OrphanedS3Page />} />
          <Route path="/ajustes" element={<SettingsPage />} />
          <Route
            path="*"
            element={
              <div className="py-10">
                <Spinner label="Cargando" />
              </div>
            }
          />
        </Route>
      </Routes>
    </ThemeProvider>
  );
}
